function formatObjectDate(date) {
  let year = date.getFullYear();
  let month = ("0" + (date.getMonth() + 1)).slice(-2);
  let day = ("0" + date.getDate()).slice(-2);
  let hours = ("0" + date.getHours()).slice(-2);
  let minutes = ("0" + date.getMinutes()).slice(-2);

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// ============================================================================
// RECURRING EVENT HELPERS - Pure Functions for Date Calculations
// ============================================================================

/**
 * Creates a date range from start/end month/year
 * @param {number} startMonth - 1-12
 * @param {number} startYear - Full year
 * @param {number} endMonth - 1-12
 * @param {number} endYear - Full year
 * @returns {Array<Date>} Array of dates in the range
 */
function calculateDateRange(startMonth, startYear, endMonth, endYear) {
  const dates = [];
  const startDate = new Date(startYear, startMonth - 1, 1);
  const endDate = new Date(endYear, endMonth - 1 + 1, 0); // Last day of end month

  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
}

/**
 * Filters dates to weekend days (Saturday=6, Sunday=0)
 * @param {Array<Date>} dates - Array of dates to filter
 * @returns {Array<Date>} Weekend dates only
 */
function filterWeekendDates(dates) {
  return dates.filter((date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  });
}

/**
 * Filters dates by specific weeks of month and days of week
 * @param {Array<Date>} dates - Array of dates to filter
 * @param {Array<number>} weeks - Week numbers (1-5)
 * @param {Array<number>} days - Day numbers (0=Sunday, 1=Monday, etc.)
 * @returns {Array<Date>} Filtered dates
 */
function filterDatesByWeeksAndDays(dates, weeks, days) {
  return dates.filter((date) => {
    const dayOfWeek = date.getDay();
    const dayOfMonth = date.getDate();
    const firstDayOfMonth = new Date(
      date.getFullYear(),
      date.getMonth(),
      1
    ).getDay();
    const weekOfMonth = Math.ceil((dayOfMonth + firstDayOfMonth) / 7);

    return weeks.includes(weekOfMonth) && days.includes(dayOfWeek);
  });
}

/**
 * Converts dates to event objects
 * @param {Array<Date>} dates - Array of dates
 * @param {string} title - Event title
 * @param {string|null} startTime - Start time (HH:mm) or null for all-day
 * @param {string|null} endTime - End time (HH:mm) or null for all-day
 * @param {boolean} isAllDay - Whether event is all-day
 * @returns {Array<Object>} Event objects
 */
function createEventObjects(dates, title, startTime, endTime, isAllDay) {
  return dates.map((date) => {
    if (isAllDay) {
      const dateStr = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      return {
        title: title,
        start: dateStr,
        end: dateStr,
      };
    } else {
      const [startHours, startMinutes] = startTime.split(":").map(Number);

      const startDateTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        startHours,
        startMinutes
      );

      let eventObj = {
        title: title,
        start: startDateTime,
      };

      if (endTime) {
        const [endHours, endMinutes] = endTime.split(":").map(Number);
        const endDateTime = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          endHours,
          endMinutes
        );
        eventObj.end = endDateTime;
      }

      return eventObj;
    }
  });
}

/**
 * MAIN ORCHESTRATOR FUNCTIONS
 */

/**
 * Generates weekend events for a date range
 * @param {Object} config - Configuration object
 * @returns {Array<Object>} Event objects
 */
function generateWeekendEvents(config) {
  const {
    startMonth,
    startYear,
    endMonth,
    endYear,
    title,
    startTime,
    endTime,
    isAllDay,
  } = config;

  const dateRange = calculateDateRange(
    startMonth,
    startYear,
    endMonth,
    endYear
  );
  const weekendDates = filterWeekendDates(dateRange);

  return createEventObjects(weekendDates, title, startTime, endTime, isAllDay);
}

/**
 * Generates custom recurring events for a date range
 * @param {Object} config - Configuration object
 * @returns {Array<Object>} Event objects
 */
function generateCustomRecurringEvents(config) {
  const {
    startMonth,
    startYear,
    endMonth,
    endYear,
    weeks,
    days,
    title,
    startTime,
    endTime,
    isAllDay,
  } = config;

  const dateRange = calculateDateRange(
    startMonth,
    startYear,
    endMonth,
    endYear
  );
  const filteredDates = filterDatesByWeeksAndDays(dateRange, weeks, days);

  return createEventObjects(filteredDates, title, startTime, endTime, isAllDay);
}

/**
 * Legacy function - kept for backwards compatibility
 * @deprecated Use generateCustomRecurringEvents instead
 */
function calculateRecurringEvents(
  year,
  month,
  weeks,
  days,
  startTime,
  endTime,
  title,
  isAllDay = false
) {
  // Convert legacy single month to new range format
  return generateCustomRecurringEvents({
    startMonth: month + 1, // JS month is 0-indexed, our new system uses 1-indexed
    startYear: year,
    endMonth: month + 1,
    endYear: year,
    weeks,
    days,
    title,
    startTime,
    endTime,
    isAllDay,
  });
}

function initializeReadOnlyCalendar(elementId, events) {
  console.log("events", events);
  const calendarObj = new EventCalendar(document.getElementById(elementId), {
    view: "dayGridMonth",
    headerToolbar: {
      start: "prev,next today",
      center: "title",
      end: "dayGridMonth,timeGridWeek,timeGridDay,",
    },
    scrollTime: "09:00:00",
    events: events.map((event) => ({
      ...event,
      title: event.name,
      start:
        event.all_day && event.start
          ? event.start.split("T")[0]
          : event.start
          ? new Date(event.start)
          : null,
      end:
        event.all_day && event.end
          ? event.end.split("T")[0]
          : event.end
          ? new Date(event.end)
          : null,
      allDay: event.all_day,
      extendedProps: { ...event, all_day: event.all_day },
      styles: ["cursor: pointer"],
    })),
    views: {
      timeGridWeek: { pointer: true },
      resourceTimeGridWeek: { pointer: true },
      resourceTimelineWeek: {
        pointer: true,
        slotMinTime: "09:00",
        slotMaxTime: "21:00",
        slotWidth: 80,
      },
    },
    eventStartEditable: false,
    eventDurationEditable: false,
    dayMaxEvents: true,
    nowIndicator: true,
    height: "1000px",
    eventClick: function (info) {
      console.log(info);
      showEventDetailsModal(info.event);
    },
  });

  $("#add-event").on("click", function () {
    openModal(
      { startStr: "", endStr: "", name: "", id: null },
      calendarObj,
      true
    );
  });

  return calendarObj;
}

document.addEventListener("turbolinks:load", function () {
  const calendarElement = $("#load_calendar");
  if (!calendarElement.length) return;

  $(".calendar-expand-button").on("click", function (event) {
    event.preventDefault();
    $(this).closest(".calendar-wrapper").toggleClass("expanded");
  });

  const isEditMode = $("#event-form").length > 0;
  const serializedEvents = $("#serialized_events").val();
  const events = serializedEvents ? JSON.parse(serializedEvents) : [];
  console.log("eventsss-", events);
  if (isEditMode) {
    const calendarObj = new EventCalendar(
      document.getElementById("load_calendar"),
      {
        view: "dayGridMonth",
        headerToolbar: {
          start: "prev,next today addEvent",
          center: "title",
          end: "dayGridMonth,timeGridWeek,timeGridDay,",
        },
        customButtons: {
          addEvent: {
            text: "Add Event",
            click: function () {
              openModal(
                { startStr: "", endStr: "", name: "", id: null },
                calendarObj,
                true
              );
            },
          },
        },
        scrollTime: "09:00:00",
        events: events.map((event) => ({
          ...event,
          title: event.name,
          start:
            event.all_day && event.start
              ? event.start.split("T")[0]
              : event.start
              ? new Date(event.start)
              : null,
          end:
            event.all_day && event.end
              ? event.end.split("T")[0]
              : event.end
              ? new Date(event.end)
              : null,
          allDay: event.all_day,
          extendedProps: { ...event, all_day: event.all_day },
        })),
        views: {
          timeGridWeek: { pointer: true },
          resourceTimeGridWeek: { pointer: true },
          resourceTimelineWeek: {
            pointer: true,
            slotMinTime: "09:00",
            slotMaxTime: "21:00",
            slotWidth: 80,
          },
        },
        dayMaxEvents: true,
        nowIndicator: true,
        selectable: true,
        height: "1000px",
        eventClick: function (info) {
          if (info.event.extendedProps.deleted) return;

          const isAllDay =
            info.event.allDay || info.event.extendedProps.all_day;
          let formattedDates;

          if (isAllDay) {
            // For all-day events, use date-only format
            const startDate = info.event.start
              ? info.event.start.toISOString().split("T")[0]
              : "";
            const endDate = info.event.end
              ? info.event.end.toISOString().split("T")[0]
              : "";
            formattedDates = {
              startStr: startDate,
              endStr: endDate,
            };
          } else {
            // For timed events, use datetime format
            formattedDates = {
              startStr: formatObjectDate(info.event.start),
              endStr: formatObjectDate(info.event.end),
            };
          }

          openModal(
            {
              ...info.event,
              ...formattedDates,
              all_day: isAllDay,
            },
            calendarObj
          );
        },
        dateClick: function (info) {
          const formattedDates = {
            startStr: info.dateStr,
            endStr: info.dateStr,
          };
          openModal({ ...info, ...formattedDates }, calendarObj, true);
          calendarObj.unselect();
        },
        select: function (info) {
          const formatDateTimeLocal = (date) => date.slice(0, 16);
          const formattedDates = {
            startStr: formatDateTimeLocal(info.startStr),
            endStr: formatDateTimeLocal(info.endStr),
          };
          openModal({ ...info, ...formattedDates }, calendarObj, true);
          calendarObj.unselect();
        },
      }
    );

    calendarObj.setOption("eventDragStop", ({ event }) => {
      calendarObj.updateEvent(setEdited(event));
    });
    calendarObj.setOption("eventResizeStop", ({ event }) => {
      calendarObj.updateEvent(setEdited(event));
    });

    $("form:first").on("submit", function (e) {
      e.preventDefault();
      const allEvents = calendarObj.getEvents();
      $("#serialized_events").val(JSON.stringify(allEvents));
      HTMLFormElement.prototype.submit.call(e.currentTarget);
    });

    initializeModalHandlers(calendarObj);

    const buttons = document.querySelectorAll("#event-form button:not([type])");
    buttons.forEach(function (button) {
      button.setAttribute("type", "button");
    });

    document.querySelectorAll("#load_calendar .ec-button").forEach((button) => {
      button.setAttribute("type", "button");
    });
  } else {
    initializeReadOnlyCalendar("load_calendar", events);
  }
});

const getInputsValue = function () {
  const inputs = $("#event-form input");
  const inputData = {};

  inputs.each(function () {
    const name = $(this).attr("name");
    if (name) {
      if ($(this).attr("type") === "checkbox") {
        inputData[name] = $(this).prop("checked");
      } else {
        inputData[name] = $(this).val();
      }
    }
  });

  inputData.event_id = $("#event_id").val();

  return inputData;
};
/**
 * UI MANAGEMENT FUNCTIONS
 */

/**
 * Updates UI based on event pattern selection
 * @param {string} pattern - "weekend" or "custom"
 */
function updateEventPatternUI(pattern) {
  const isWeekend = pattern === "weekend";
  const customSettings = $("#custom-schedule-settings");

  if (isWeekend) {
    // Hide custom schedule settings for weekend events
    customSettings.addClass("hidden");
  } else {
    // Show custom schedule settings for custom events
    customSettings.removeClass("hidden");
    // Reset checkboxes when switching to custom
    $(".week-checkbox").prop("checked", false);
    $(".day-checkbox").prop("checked", false);
  }
}

/**
 * Validates recurring event form data
 * @param {Object} formData - Form data object
 * @returns {Object} Validation result with isValid and message
 */
function validateRecurringEventForm(formData) {
  const {
    eventName,
    eventPattern,
    startMonth,
    endMonth,
    startYear,
    endYear,
    isAllDay,
    startTime,
    endTime,
    weeks,
    days,
  } = formData;

  if (!eventName) {
    return { isValid: false, message: "Please enter an event name." };
  }

  if (!startMonth || !startYear || !endMonth || !endYear) {
    return { isValid: false, message: "Please select start and end months." };
  }

  if (new Date(startYear, startMonth - 1) > new Date(endYear, endMonth - 1)) {
    return { isValid: false, message: "End date must be after start date." };
  }

  if (!isAllDay && !startTime) {
    return {
      isValid: false,
      message: "Please enter a start time for timed events.",
    };
  }

  if (eventPattern === "custom" && (weeks.length === 0 || days.length === 0)) {
    return {
      isValid: false,
      message: "Please select weeks and days for custom events.",
    };
  }

  return { isValid: true, message: "" };
}

const onCreateEvent = function (calendarObj) {
  const weeklyFieldsVisible = !$("#weekly-recurrence-fields").hasClass(
    "hidden"
  );

  if (weeklyFieldsVisible) {
    const formData = {
      eventName: $("#event_name").val(),
      eventPattern: $('input[name="event_pattern"]:checked').val(),
      startMonth: parseInt($("#event_start_month").val()),
      startYear: parseInt($("#event_start_year").val()),
      endMonth: parseInt($("#event_end_month").val()),
      endYear: parseInt($("#event_end_year").val()),
      isAllDay: $("#event_all_day").prop("checked"),
      startTime: $("#event_start_time").val(),
      endTime: $("#event_end_time").val(),
      weeks: $(".week-checkbox:checked")
        .map((_, el) => parseInt($(el).val()))
        .get(),
      days: $(".day-checkbox:checked")
        .map((_, el) => parseInt($(el).val()))
        .get(),
    };

    const validation = validateRecurringEventForm(formData);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    let events;
    if (formData.eventPattern === "weekend") {
      events = generateWeekendEvents({
        startMonth: formData.startMonth,
        startYear: formData.startYear,
        endMonth: formData.endMonth,
        endYear: formData.endYear,
        title: formData.eventName,
        startTime: formData.startTime,
        endTime: formData.endTime,
        isAllDay: formData.isAllDay,
      });
    } else {
      events = generateCustomRecurringEvents({
        startMonth: formData.startMonth,
        startYear: formData.startYear,
        endMonth: formData.endMonth,
        endYear: formData.endYear,
        weeks: formData.weeks,
        days: formData.days,
        title: formData.eventName,
        startTime: formData.startTime,
        endTime: formData.endTime,
        isAllDay: formData.isAllDay,
      });
    }

    events.forEach((event) => {
      calendarObj.addEvent({
        title: event.title,
        start: event.start,
        end: event.end,
        allDay: formData.isAllDay,
        extendedProps: {
          edited: true,
          all_day: formData.isAllDay,
          recurring: true,
        },
      });
    });
  } else {
    let inputData = getInputsValue();
    const eventId = inputData.event_id;
    const event = calendarObj.getEventById(eventId);
    const isAllDay = inputData["event[all_day]"];

    // Validate required fields
    if (!inputData["event[name]"]) {
      alert("Please enter an event name.");
      return;
    }

    if (!inputData["event[start]"]) {
      alert("Please enter a start date/time.");
      return;
    }

    let eventData = {
      id: eventId,
      title: inputData["event[name]"],
      allDay: isAllDay,
      extendedProps: {
        edited: true,
        all_day: isAllDay,
      },
    };

    if (isAllDay) {
      // For all-day events, the inputs are already date-only
      const startDate = inputData["event[start]"];
      const endDate = inputData["event[end]"];

      if (startDate) {
        eventData.start = startDate;
        eventData.end = endDate || startDate; // Use start date if no end date provided
      }
    } else {
      // For timed events, use datetime-local values
      eventData.start = inputData["event[start]"];
      // If no end time provided, set end time to null (open-ended event)
      eventData.end = inputData["event[end]"] || null;
    }

    if (event) {
      calendarObj.updateEvent(eventData);
    } else {
      calendarObj.addEvent(eventData);
    }
  }

  $.modal.close();
};
const setValues = function (values) {
  if (!values) return;

  $('[name="event[name]"]').val(values.name || values.title);
  $("#event_id").val(values.id || new Date());

  // Set all-day checkbox first
  const isAllDay = values.all_day || values.allDay;
  $("#event_all_day").prop("checked", isAllDay);

  // Toggle time fields visibility (this changes the input types)
  toggleTimeFields(!isAllDay);

  // Set start/end values after input types are set correctly
  if (values.startStr) {
    if (isAllDay) {
      // For all-day events, extract just the date part
      const startDate = values.startStr.includes("T")
        ? values.startStr.split("T")[0]
        : values.startStr;
      $('[name="event[start]"]').val(startDate);
    } else {
      // For timed events, use the full datetime value
      $('[name="event[start]"]').val(values.startStr);
    }
  }

  if (values.endStr) {
    if (isAllDay) {
      // For all-day events, extract just the date part
      const endDate = values.endStr.includes("T")
        ? values.endStr.split("T")[0]
        : values.endStr;
      $('[name="event[end]"]').val(endDate);
    } else {
      // For timed events, use the full datetime value
      $('[name="event[end]"]').val(values.endStr);
    }
  }
};

const setEdited = (event) => ({
  ...event,
  extendedProps: {
    ...event.extendedProps,
    edited: true,
  },
});

const toggleTimeFields = function (showTime) {
  const startInput = $('[name="event[start]"]');
  const endInput = $('[name="event[end]"]');
  const startLabel = $('label[for="event_start"]');
  const endLabel = $('label[for="event_end"]');

  if (showTime) {
    // Show time fields - use datetime-local inputs
    startInput.attr("type", "datetime-local").prop("required", true);
    endInput.attr("type", "datetime-local").prop("required", false); // End time is optional
    startLabel.text("Start Time");
    endLabel.text("End Time (optional - leave blank if uncertain)");
    $("#weekly-recurrence-fields input[type='time']").prop("required", false);
    $("#weekly-recurrence-fields .time-section").show();
  } else {
    // Hide time fields - use date inputs only
    startInput.attr("type", "date").prop("required", true);
    endInput.attr("type", "date").prop("required", false); // End date is optional for all-day
    startLabel.text("Start Date");
    endLabel.text("End Date (optional)");
    $("#weekly-recurrence-fields input[type='time']").prop("required", false);
    $("#weekly-recurrence-fields .time-section").hide();
  }
};

const openModal = function (event, calendarObj, isCreating = false) {
  $("#single-event-btn").addClass("pure-button-primary");
  $("#recurring-event-btn").removeClass("pure-button-primary");
  $("#manual-date-fields").removeClass("hidden");
  $("#weekly-recurrence-fields").addClass("hidden");

  // Reset multi-date picker
  $("#multi-date-picker").addClass("hidden");

  // Reset recurring event fields
  const now = new Date();
  $("#event_start_month").val(now.getMonth() + 1);
  $("#event_start_year").val(now.getFullYear());
  $("#event_end_month").val(now.getMonth() + 1);
  $("#event_end_year").val(now.getFullYear());

  // Reset pattern selection
  $("#weekend_event").prop("checked", true);
  $("#custom_event").prop("checked", false);

  // Reset custom schedule settings
  $(".week-checkbox").prop("checked", false);
  $(".day-checkbox").prop("checked", false);
  $("#custom-schedule-settings").addClass("hidden");
  $('#weekly-recurrence-fields input[type="time"]').val("");

  // Reset all-day checkbox
  $("#event_all_day").prop("checked", false);
  toggleTimeFields(true);

  if (!event.id) {
    $("#delete-event").addClass("hidden");
  } else {
    $("#delete-event").removeClass("hidden");
  }
  if (!isCreating) {
    setValues({
      name: event.title,
      startStr: event.startStr,
      endStr: event.endStr,
      id: event.id,
      all_day: event.all_day || event.extendedProps?.all_day || event.allDay,
    });
  } else {
    setValues(event);
  }

  setTimeout(updateSubmitButtonState, 0);

  $("#event-form").modal();
};

function showEventDetailsModal(event) {
  console.log("Showing modal for event:", event);

  $("#event-details-modal").remove();

  const isAllDay = event.allDay || event.extendedProps?.all_day;

  const dateOptions = isAllDay
    ? {
        weekday: "short",
        month: "short",
        day: "numeric",
      }
    : {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      };

  const startDate = new Date(event.start).toLocaleDateString(
    "en-US",
    dateOptions
  );
  const endDate = new Date(event.end).toLocaleDateString("en-US", dateOptions);

  const modalHTML = `
    <div id="event-details-modal" class="modal l-admin-container">
      <div class="event-details-content-wrapper">
        <div class="event-details-content">
          <h2 class="event-title">${event.title}</h2>

          <div class="event-info">
            <h3>Event Details</h3>
            <p><strong>When:</strong></p>
            <p>Starts: ${startDate}</p>
            <p>Ends: ${endDate}</p>

            <p><strong>Where:</strong></p>
            <p>${
              event.extendedProps.location_state || "Location not specified"
            }</p>
          </div>

          <div class="listing-info">
            <h3>Listing Information</h3>
            <p><strong>Tribe:</strong> ${
              event.extendedProps.tribe_name || "Not specified"
            }</p>
            <p><strong>Description:</strong></p>
            <p>${
              event.extendedProps.description || "No description available"
            }</p>

            ${
              event.extendedProps.listing_phone
                ? `<p><strong>Contact:</strong> ${event.extendedProps.listing_phone}</p>`
                : ""
            }
          </div>


        </div>
        <div class="modal-footer">
            <a href="/listings/${event.extendedProps.listing_slug}"
              class="btn pure-button admin-form-button pure-button-primary">
              View Full Listing
            </a>
          </div>
      </div>
    </div>
  `;

  $(modalHTML).appendTo("body");

  $("#event-details-modal").modal({
    fadeDuration: 250,
    showClose: true,
  });
}

const style = document.createElement("style");
style.textContent = `
  #event-details-modal {
    max-width: 600px;
    padding: 20px;
  }
  .event-details-content {
    overflow-y: scroll;
    max-height: 80vh;
  }

  #event-details-modal .event-title {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    color: #333;
  }

  #event-details-modal .event-info,
  #event-details-modal .listing-info {
    margin-bottom: 20px;
  }

  #event-details-modal h3 {
    color: #444;
    margin-bottom: 10px;
  }

  #event-details-modal p {
    margin: 5px 0;
    line-height: 1.4;
  }

  #event-details-modal .modal-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
    text-align: right;
  }

  #event-details-modal strong {
    color: #555;
  }

  /* Make events more clearly editable in edit mode */
  .ec-event {
    cursor: pointer !important;
  }

  .ec-event:hover {
    opacity: 0.8;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  /* Add edit helper text */
  .event-edit-helper {
    background: #e3f2fd;
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 15px;
    font-size: 14px;
    color: #1976d2;
    border-left: 4px solid #1976d2;
  }
`;
document.head.appendChild(style);

function validateModalForm() {
  const eventName = $("#event_name").val();
  const isWeeklyMode = !$("#weekly-recurrence-fields").hasClass("hidden");

  if (isWeeklyMode) {
    // For recurring events, just check if name exists
    return eventName.trim().length > 0;
  } else {
    // For single events, check name and start date
    const startDate = $('[name="event[start]"]').val();

    const hasName = eventName.trim().length > 0;
    const hasStartDate = startDate.trim().length > 0;

    return hasName && hasStartDate; // End date/time is optional
  }
}

function updateSubmitButtonState() {
  const isValid = validateModalForm();
  $("#grab-values").prop("disabled", !isValid);
}

function initializeModalHandlers(calendarObj) {
  // Initialize multi-date selection state
  let selectedDates = [];

  $("#event_name, [name='event[start]'], [name='event[end]']").on(
    "input change",
    function () {
      updateSubmitButtonState();
    }
  );

  $("#grab-values").on("click", function () {
    onCreateEvent(calendarObj);
  });

  $("#clear-end-time").on("click", function () {
    $('[name="event[end]"]').val("");
    updateSubmitButtonState();
  });

  $("#clear-recurring-start-time").on("click", function () {
    $("#event_start_time").val("");
  });

  $("#clear-recurring-end-time").on("click", function () {
    $("#event_end_time").val("");
  });

  $("#quick-select-this-weekend").on("click", function () {
    const today = new Date();
    const dayOfWeek = today.getDay();

    let daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    if (daysUntilSaturday === 0 && dayOfWeek !== 6) daysUntilSaturday = 7; // If today is not Saturday, get next Saturday

    const saturday = new Date(today);
    saturday.setDate(today.getDate() + daysUntilSaturday);

    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() + 1);

    const isAllDay = $("#event_all_day").prop("checked");
    if (isAllDay) {
      $('[name="event[start]"]').val(formatDate(saturday));
      $('[name="event[end]"]').val(formatDate(sunday));
    } else {
      $('[name="event[start]"]').val(formatDateTime(saturday));
      $('[name="event[end]"]').val(formatDateTime(sunday));
    }
    updateSubmitButtonState();
  });

  $("#quick-select-next-weekend").on("click", function () {
    const today = new Date();
    const dayOfWeek = today.getDay();

    let daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    if (daysUntilSaturday === 0) daysUntilSaturday = 7; // If today is Saturday, get next Saturday
    daysUntilSaturday += 7; // Add a week to get "next" weekend

    const saturday = new Date(today);
    saturday.setDate(today.getDate() + daysUntilSaturday);

    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() + 1);

    const isAllDay = $("#event_all_day").prop("checked");
    if (isAllDay) {
      $('[name="event[start]"]').val(formatDate(saturday));
      $('[name="event[end]"]').val(formatDate(sunday));
    } else {
      $('[name="event[start]"]').val(formatDateTime(saturday));
      $('[name="event[end]"]').val(formatDateTime(sunday));
    }
    updateSubmitButtonState();
  });

  // Quick select: Choose Specific Dates
  $("#quick-select-choose-dates").on("click", function () {
    $("#multi-date-picker").toggleClass("hidden");
    if (!$("#multi-date-picker").hasClass("hidden")) {
      initializeDatePickerCalendar();
    }
  });

  // Apply selected dates
  $("#apply-selected-dates").on("click", function () {
    if (selectedDates.length === 0) {
      alert("Please select at least one date.");
      return;
    }

    // Create events for each selected date
    const eventName = $("#event_name").val();
    if (!eventName) {
      alert("Please enter an event name first.");
      return;
    }

    const isAllDay = $("#event_all_day").prop("checked");
    const startTime = $('[name="event[start]"]').val();
    const endTime = $('[name="event[end]"]').val();

    // Get just the time part if it's a timed event and times are provided
    let timeStart = null;
    let timeEnd = null;
    if (!isAllDay && startTime) {
      timeStart = startTime.includes("T") ? startTime.split("T")[1] : null;
      timeEnd = endTime && endTime.includes("T") ? endTime.split("T")[1] : null;
    }

    selectedDates.forEach((dateStr) => {
      let eventData = {
        id: new Date().getTime() + Math.random(), // Unique ID
        title: eventName,
        extendedProps: {
          edited: true,
        },
      };

      if (isAllDay) {
        // All-day event
        eventData.allDay = true;
        eventData.start = dateStr;
        eventData.end = dateStr;
        eventData.extendedProps.all_day = true;
      } else if (timeStart) {
        // Timed event with specific start time
        eventData.allDay = false;
        eventData.start = `${dateStr}T${timeStart}`;
        if (timeEnd) {
          eventData.end = `${dateStr}T${timeEnd}`;
        }
        // If no end time, don't set it (open-ended)
        eventData.extendedProps.all_day = false;
      } else {
        // No specific time provided - treat as all-day
        eventData.allDay = true;
        eventData.start = dateStr;
        eventData.end = dateStr;
        eventData.extendedProps.all_day = true;
      }

      calendarObj.addEvent(eventData);
    });

    // Reset and close
    selectedDates = [];
    $("#multi-date-picker").addClass("hidden");
    $.modal.close();
  });

  // Helper function to initialize the date picker calendar
  function initializeDatePickerCalendar() {
    const calendar = $("#date-picker-calendar");
    calendar.empty();
    selectedDates = [];

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Create a simple calendar grid
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const calendarHTML = `
      <div style="text-align: center; margin-bottom: 10px;">
        <strong>${monthNames[currentMonth]} ${currentYear}</strong>
      </div>
      <div id="calendar-grid" style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px;">
        <div style="text-align: center; font-weight: bold; padding: 5px;">Sun</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Mon</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Tue</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Wed</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Thu</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Fri</div>
        <div style="text-align: center; font-weight: bold; padding: 5px;">Sat</div>
      </div>
    `;
    calendar.html(calendarHTML);

    const grid = $("#calendar-grid");
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    for (let i = 0; i < firstDay; i++) {
      grid.append("<div></div>");
    }

    // Add day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;
      const dayCell = $(`
        <div class="date-cell" data-date="${dateStr}"
             style="text-align: center; padding: 8px; cursor: pointer; border: 1px solid #dee2e6; border-radius: 4px; background: white;">
          ${day}
        </div>
      `);

      dayCell.on("click", function () {
        const date = $(this).data("date");
        const index = selectedDates.indexOf(date);

        if (index > -1) {
          selectedDates.splice(index, 1);
          $(this).css("background", "white");
        } else {
          selectedDates.push(date);
          $(this).css("background", "#007bff").css("color", "white");
        }

        updateSelectedDatesDisplay();
      });

      grid.append(dayCell);
    }
  }

  function updateSelectedDatesDisplay() {
    const display = $("#selected-dates-display");
    if (selectedDates.length === 0) {
      display.html(
        '<span style="color: #6c757d; font-size: 13px;">No dates selected</span>'
      );
    } else {
      const datesList = selectedDates
        .sort()
        .map((date) => {
          const d = new Date(date + "T12:00:00");
          return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        })
        .join(", ");
      display.html(
        `<span style="color: #495057; font-size: 13px;"><strong>Selected:</strong> ${datesList}</span>`
      );
    }
  }

  function formatDate(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  }

  function formatDateTime(date) {
    return `${formatDate(date)}T${String(date.getHours()).padStart(
      2,
      "0"
    )}:${String(date.getMinutes()).padStart(2, "0")}`;
  }

  // Handle all-day checkbox change
  $("#event_all_day").on("change", function () {
    const isAllDay = $(this).prop("checked");
    toggleTimeFields(!isAllDay);
    updateSubmitButtonState();
  });

  $("#single-event-btn").on("click", function () {
    if ($(this).hasClass("pure-button-primary")) return; // Already active

    $(this).addClass("pure-button-primary");
    $("#recurring-event-btn").removeClass("pure-button-primary");

    $("#manual-date-fields").removeClass("hidden");
    $("#weekly-recurrence-fields").addClass("hidden");

    // Check if all-day is selected and toggle input types accordingly
    const isAllDay = $("#event_all_day").prop("checked");
    toggleTimeFields(!isAllDay);
    updateSubmitButtonState();
  });

  $("#recurring-event-btn").on("click", function () {
    if ($(this).hasClass("pure-button-primary")) return; // Already active

    $(this).addClass("pure-button-primary");
    $("#single-event-btn").removeClass("pure-button-primary");

    $("#manual-date-fields").addClass("hidden");
    $("#weekly-recurrence-fields").removeClass("hidden");

    // Initialize with current date
    const now = new Date();
    $("#event_start_month").val(now.getMonth() + 1);
    $("#event_start_year").val(now.getFullYear());
    $("#event_end_month").val(now.getMonth() + 1);
    $("#event_end_year").val(now.getFullYear());

    // Reset to weekend event pattern
    $("#weekend_event").prop("checked", true);
    updateEventPatternUI("weekend");

    // Check if all-day is selected and hide time fields if so
    const isAllDay = $("#event_all_day").prop("checked");
    if (isAllDay) {
      $("#weekly-recurrence-fields .time-section").hide();
    } else {
      $("#weekly-recurrence-fields .time-section").show();
    }
    updateSubmitButtonState();
  });

  // Event pattern change handlers
  $('input[name="event_pattern"]').on("change", function () {
    const pattern = $(this).val();
    updateEventPatternUI(pattern);
  });

  // No longer needed - advanced settings are controlled by radio buttons

  // Week checkboxes now always show days section (no need for show/hide logic)

  $("#delete-event").on("click", function () {
    const eventId = $("#event_id").val();
    const event = calendarObj.getEventById(eventId);
    const solidItems = JSON.parse($("#serialized_events").val() || "[]");

    if (solidItems.some((item) => item.id === parseInt(eventId))) {
      event.extendedProps.deleted = true;
      event.backgroundColor = "#f1f1f1";
      calendarObj.updateEvent(event);
    } else {
      calendarObj.removeEventById(eventId);
    }

    $.modal.close();
  });
}
